import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  InputAdornment,
  LinearProgress,
  Typography,
} from '@mui/material';
import { checkDuplicateAccount, sendCombineAccountsEmail } from 'api';
import { RegisterUserInfo, completedRegistration } from 'api/login';
import { TOAST_TYPE, ToastContext } from 'context/toastContext';
import { ChangeEvent, ReactNode, useContext, useEffect, useState } from 'react';
import React from 'react';
import { TextValidator, ValidatorForm } from 'react-material-ui-form-validator';
import { NavigateFunction, useNavigate, useParams } from 'react-router';
import { ROUTES, routeWithParams } from 'router';

import { LoadingButton } from '@mui/lab';
import AuturaTextField from 'components/inputs/AuturaTextField';
import { ThemeContext } from 'context/themeContext';
import { useTranslation } from 'react-i18next';
import { Status } from 'types';
import { getCookieValue, logError } from 'utils';
import AuthService from 'utils/AuthService';

const Registration = () => {
  const navigate: NavigateFunction = useNavigate();
  const { t } = useTranslation();
  const displayToast = useContext(ToastContext);
  const { logos } = useContext(ThemeContext);
  const { token } = useParams();

  let claims = { firstName: '', lastName: '', email: '', id: 0 };
  try {
    claims = AuthService.DecodeJWT(token || AuthService.GetToken());
  } catch (e) {
    logError(e as Error);
  }

  const [formState, setFormState] = useState<RegisterUserInfo>({
    firstName: claims.firstName,
    lastName: claims.lastName,
    email: claims.email,
    password: '',
    passwordConfirm: '',
    id: claims.id,
    status: Status.Active,
  });

  useEffect(() => {
    if (claims.id === 0) {
      const companyPath = getCookieValue('companyPath');
      navigate(routeWithParams(ROUTES.LOGIN, [companyPath]));
    }
  }, []);

  const [meter, setMeter] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showAlert, setShowAlert] = useState<boolean>(false);

  const [loginError, setLoginError] = useState<string>('');
  const [duplicateAccount, setDuplicateAccount] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [showPasswordConfirm, setShowPasswordConfirm] =
    useState<boolean>(false);
  const [doPasswordsMatch, setPasswordsMatch] = useState<boolean>(false);
  const [confirmMessage, setShowConfirmMessage] = useState<boolean>(false);
  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };
  const handleClickShowPasswordConfirm = () => {
    setShowPasswordConfirm(!showPasswordConfirm);
  };

  const atLeastOneUppercase = /[A-Z]/g; // capital letters from A to Z
  const atLeastOneLowercase = /[a-z]/g; // small letters from a to z
  const atLeastOneNumeric = /\d/g; // numbers from 0 to 9
  const atLeastOneSpecialChar = /[#?!@$%^&*-]/g; // any of the special characters within the square brackets
  const eightCharsOrMore = /.{8,}/g; // eight characters or more

  const passwordTracker = {
    uppercase: formState.password.match(atLeastOneUppercase),
    lowercase: formState.password.match(atLeastOneLowercase),
    number: formState.password.match(atLeastOneNumeric),
    specialChar: formState.password.match(atLeastOneSpecialChar),
    eightCharsOrGreater: formState.password.match(eightCharsOrMore),
  };

  const passwordStrength = Object.values(passwordTracker).filter(
    (value) => value,
  ).length;

  const getMeterStyle = (): string => {
    return `
          .password-strength-meter {
            height: 18px;
            margin-y: 10px;
            background-color: lightgrey;
            border-radius: 3px;
            margin: .5rem 0
          }
          .password-strength-meter::before {
            content: "";
            background-color: ${
              ['red', 'orange', '#03a2cc', '#03a2cc', '#0ce052'][
                passwordStrength - 1
              ] || ''
            };
            height: 100%;
            display: block;
            border-radius: 3px;
            transition: width 0.2s;`;
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormState({
      ...formState,
      [name]: value,
    });
  };

  useEffect(() => {
    if (
      formState.password.match(atLeastOneUppercase) &&
      formState.password.match(atLeastOneLowercase) &&
      formState.password.match(atLeastOneNumeric) &&
      formState.password.match(atLeastOneSpecialChar) &&
      formState.password.match(eightCharsOrMore)
    ) {
      setShowConfirmMessage(true);
      if (formState.password === formState.passwordConfirm) {
        setPasswordsMatch(true);
      } else {
        setPasswordsMatch(false);
      }
    } else {
      setShowConfirmMessage(false);
    }
  }, [formState.password, formState.passwordConfirm]);

  const handleCheckDuplicateAccount = async () => {
    setIsSubmitting(true);
    try {
      const res = await checkDuplicateAccount({
        email: formState.email,
        token: token || AuthService.GetToken(),
      });

      // if activeAccount = true means the account exists with the same email.
      if (!res.ok) {
        const { message } = JSON.parse(await res.text());
        setShowAlert(true);
        if (res.status === 400 || res.status === 401) {
          if (message.search(new RegExp('expired', 'gi'))) {
            setLoginError(
              `This link is expired. Contact an administrator to receive a new one.`,
            );
          } else if (message.search(new RegExp('invalid', 'gi'))) {
            setLoginError(
              `This link is invalid. Contact an administrator to receive a new one.`,
            );
          } else {
            setLoginError(`Status: ${res.status} ${message}`);
          }
        } else {
          setLoginError(`Status: ${res.status} ${message}`);
        }
        setIsSubmitting(false);
      } else {
        const response = await res.json();
        setDuplicateAccount(response.activeAccount);
        setLoginError(
          response.activeAccount
            ? `Status: Email address already in use would you like to combine them or use a different?`
            : '',
        );
        setIsSubmitting(false);

        if (!response.activeAccount) {
          await handleRegisterForm();
        }
      }

      setIsSubmitting(false);
    } catch (e) {
      logError(e as Error);
      displayToast({
        type: TOAST_TYPE.ERROR,
        message: t('registration.error'),
      });
      setIsSubmitting(false);
    }
  };

  const clearEmail = async () => {
    setDuplicateAccount(false);
    setFormState({
      ...formState,
      email: '',
    });
  };

  const handleSendCombineAccountEmail = async () => {
    await sendCombineAccountsEmail({
      email: formState.email,
      token: token || AuthService.GetToken(),
    });
    setDuplicateAccount(false);
    displayToast({
      type: TOAST_TYPE.SUCCESS,
      message: t('registration.emailSent', { email: formState.email }),
    });
    const companyPath = getCookieValue('companyPath');
    // navigate user to check email page
    navigate(routeWithParams(ROUTES.LOGIN, [companyPath]));
  };

  const handleRegisterForm = async () => {
    setIsSubmitting(true);
    formState.status = Status.Active;
    try {
      if (!isMissingInfo()) {
        const res = await completedRegistration(
          formState,
          claims.id,
          token || AuthService.GetToken(),
          duplicateAccount,
        );
        if (!res.ok) {
          const { message } = JSON.parse(await res.text());
          displayToast({
            type: TOAST_TYPE.ERROR,
            message: `Error registering user: (${message})`,
          });
        } else {
          displayToast({
            type: TOAST_TYPE.SUCCESS,
            message: t('registration.success'),
          });
          const companyPath = getCookieValue('companyPath');
          // navigate user to check email page
          navigate(routeWithParams(ROUTES.LOGIN, [companyPath]));
        }
      }
    } catch (e) {
      logError(e as Error);
      displayToast({
        type: TOAST_TYPE.ERROR,
        message: t('registration.error'),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const isMissingInfo = (): boolean => {
    return !formState.firstName
      ? !formState.firstName
      : !formState.lastName
        ? !formState.lastName
        : !doPasswordsMatch
          ? !doPasswordsMatch
          : isSubmitting;
  };

  const renderErrorNotification = (): ReactNode | string => {
    return showAlert ? (
      <Alert severity='error' onClose={() => setShowAlert(false)}>
        <AlertTitle>{t('error')}</AlertTitle>
        <strong>{loginError}</strong>
      </Alert>
    ) : (
      ''
    );
  };

  const renderDuplicateAccountDialog = (): ReactNode => {
    return (
      <Dialog open={duplicateAccount}>
        <DialogTitle id='alert-dialog-title'>
          {t('registration.emailInUse')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id='alert-dialog-description'>
            {t('registration.duplicateAccountMessage')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={clearEmail}>{t('registration.changeEmail')}</Button>
          <Button onClick={handleSendCombineAccountEmail} autoFocus>
            {t('combine')}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <React.Fragment>
      {renderDuplicateAccountDialog()}

      <Card
        raised
        sx={{
          backgroundColor: 'white',
          flex: 1,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <style>{getMeterStyle()}</style>
        <CardMedia
          component='img'
          image={logos.logoUrl}
          alt='logo'
          sx={{
            px: `${logos.logoPadding}px`,
            pt: `${logos.logoPadding}px`,
          }}
        />
        <CardContent
          sx={{
            padding: {
              xs: 2,
              sm: 4,
            },
            height: {
              xs: '100%',
              sm: 'auto',
            },
            mb: {
              xs: 8,
              sm: 0,
            },
          }}
        >
          {renderErrorNotification()}
          <Container component='main' maxWidth='xs'>
            <Typography
              component='h5'
              variant='h5'
              align='center'
              sx={{ py: 1 }}
            >
              {t('registration.title')}
            </Typography>
            <ValidatorForm component='form' onSubmit={handleRegisterForm}>
              <AuturaTextField
                required
                name='firstName'
                onChange={handleChange}
                value={formState.firstName}
              />
              <AuturaTextField
                required
                name='lastName'
                onChange={handleChange}
                value={formState.lastName}
              />
              <AuturaTextField
                required
                value={formState.email}
                name='email'
                validators={['isEmail']}
                errorMessages={[t('email.invalid', { name: t('email') })]}
                onChange={handleChange}
              />
              <TextValidator
                fullWidth
                margin={'normal'}
                value={formState.password}
                onFocus={() => setMeter(true)}
                onChange={handleChange}
                name='password'
                label={t('password') + ' *'}
                type={showPassword ? 'text' : 'password'}
                validators={['required']}
                errorMessages={['Password is required']}
                endAdornment={
                  <InputAdornment position='end'>
                    <IconButton
                      aria-label='toggle password visibility'
                      onClick={handleClickShowPassword}
                      edge='end'
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
              {meter && (
                <Box sx={{ mb: 2 }}>
                  <div className='password-strength-meter'></div>
                  <Typography
                    variant={'caption'}
                    style={{ fontWeight: 'bold' }}
                  >
                    {passwordStrength < 5 && `${t('registration.mustContain')}`}
                    {!passwordTracker.uppercase &&
                      `${t('registration.uppercase')}, `}
                    {!passwordTracker.lowercase &&
                      `${t('registration.lowercase')}, `}
                    {!passwordTracker.specialChar &&
                      `${t('registration.special')}, `}
                    {!passwordTracker.number && `${t('registration.number')}, `}
                    {!passwordTracker.eightCharsOrGreater &&
                      `${t('registration.eightOrMore')}`}
                  </Typography>
                </Box>
              )}
              <TextValidator
                type={showPasswordConfirm ? 'text' : 'password'}
                fullWidth
                margin={'normal'}
                variant='outlined'
                value={formState.passwordConfirm}
                onChange={handleChange}
                name='passwordConfirm'
                validators={['required']}
                errorMessages={[t('error.required', { name: t('password') })]}
                label={t('registration.confirmPassword') + ' *'}
                helperText={
                  confirmMessage &&
                  (doPasswordsMatch
                    ? t('registration.match')
                    : t('registration.noMatch'))
                }
                endAdornment={
                  <InputAdornment position='end'>
                    <IconButton
                      aria-label='toggle password visibility'
                      onClick={handleClickShowPasswordConfirm}
                      edge='end'
                    >
                      {showPasswordConfirm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
              />
              <LoadingButton
                fullWidth
                color='primary'
                type='submit'
                variant='contained'
                sx={{ my: 2, py: 1 }}
                onClick={handleCheckDuplicateAccount}
                loading={isSubmitting}
                loadingIndicator={
                  <LinearProgress
                    style={{ width: 50, height: 5 }}
                    color='primary'
                  />
                }
                disabled={isMissingInfo()}
              >
                {t('registration.title')}
              </LoadingButton>
            </ValidatorForm>
          </Container>
        </CardContent>
      </Card>
    </React.Fragment>
  );
};

export default Registration;
